import type { BusinesscaseRevenue, CalculationResults, BusinesscaseExtraCost } from '@/types/businesscase';

interface CalculationInputs {
  costs: {
    batteryPurchaseCost: number;
    installationCost: number;
    softwareSetupCost: number;
    annualSubscription: number;
    otherCosts: number;
  };
  subsidies: {
    eiaEnabled: boolean;
    flexEEnabled: boolean;
    flexEValue: number;
    otherSubsidy: number;
    taxRate: number;
  };
  revenues: BusinesscaseRevenue[];
  extraCosts?: BusinesscaseExtraCost[];
  roiMode?: 'bruto' | 'netto';
}

/**
 * Calculate all financial metrics for a businesscase
 * Pure function - all business logic isolated here for reusability
 */
export function calculateBusinesscase(inputs: CalculationInputs): CalculationResults {
  const { costs, subsidies, revenues, extraCosts = [], roiMode = 'bruto' } = inputs;

  // 1. Calculate extra costs total
  const totalExtraCosts = extraCosts.reduce((sum, cost) => sum + cost.amount, 0);

  // 2. Total Investment (sum of all cost fields including annual subscription for year 0)
  const totalInvestment = 
    costs.batteryPurchaseCost +
    costs.installationCost +
    costs.softwareSetupCost +
    costs.annualSubscription +  // Year 0 subscription is part of CAPEX
    costs.otherCosts +
    totalExtraCosts;

  // 3. EIA Value (40% aftrek × tax rate = netto voordeel)
  // EIA gives 40% deduction from taxable profit, which at 25% tax rate = 10% net benefit
  const eiaValue = subsidies.eiaEnabled
    ? (totalInvestment * 0.40) * (subsidies.taxRate / 100)
    : 0;

  // 4. Net Investment (after subsidies)
  // Only subtract Flex-E if enabled
  const flexESubsidy = subsidies.flexEEnabled ? subsidies.flexEValue : 0;
  const netInvestment = totalInvestment - eiaValue - subsidies.otherSubsidy - flexESubsidy;

  // 4. Sort revenues by year
  const sortedRevenues = [...revenues].sort((a, b) => a.year - b.year);

  // 5. Calculate yearly data with cumulative cashflow
  let cumulativeCashflow = -netInvestment; // Start with negative investment
  let paybackPeriod: number | null = null;
  let previousCumulativeCashflow = -netInvestment;
  
  const yearlyData = sortedRevenues.map((rev, index) => {
    // Calculate gross yearly revenue (all income streams, EXCLUDING costs)
    const yearRevenue = 
      (rev.grid_connection_savings || 0) +
      (rev.grid_purchase_cost_savings || 0) +
      (rev.dayahead_incomes || 0) +
      (rev.hbe_incomes || 0) +
      (rev.energy_tax_savings || 0) +
      (rev.endex_cost_savings || 0) +
      (rev.imbalance_income || 0) +
      (rev.imbalance_discount || 0) +
      (rev.generator_cost_savings || 0) +
      (rev.extra_sde || 0) +
      (rev.total_solar_revenues || 0);  // Solar revenues included
      // NOTE: ems_costs and total_opex are NOT included here anymore
    
    // OPEX for chart display (show as positive costs)
    const yearOpex = 
      Math.abs(rev.ems_costs || 0) +
      Math.abs(rev.total_opex || 0);
    
    // Net cashflow: revenue minus costs
    const yearCashflow = yearRevenue - yearOpex;
    
    // Store previous cumulative for interpolation
    previousCumulativeCashflow = cumulativeCashflow;
    
    // Update cumulative cashflow
    cumulativeCashflow += yearCashflow;
    
    // Check if this is the payback year (first year cumulative > 0)
    // Use linear interpolation for more accurate decimal payback period
    if (cumulativeCashflow >= 0 && paybackPeriod === null) {
      if (previousCumulativeCashflow < 0 && yearCashflow > 0) {
        // Linear interpolation: how far into this year did we break even?
        const fractionOfYear = Math.abs(previousCumulativeCashflow) / yearCashflow;
        paybackPeriod = Math.round((index + fractionOfYear) * 10) / 10; // Round to 1 decimal
      } else {
        paybackPeriod = index + 1;
      }
    }
    
    return {
      year: rev.year,
      revenue: yearRevenue,
      opex: yearOpex,
      cashflowBase: yearCashflow,
      cashflowBest: yearCashflow * 1.15, // +15% best case
      cashflowWorst: yearCashflow * 0.90, // -10% worst case
      cumulativeCashflow
    };
  });

  // 6. Total Revenues (sum of all yearly revenues)
  const totalRevenues = yearlyData.reduce((sum, year) => sum + year.revenue, 0);

  // 7. Total OPEX
  const totalOpex = yearlyData.reduce((sum, year) => sum + year.opex, 0);

  // 8. Total Cashflow
  const totalCashflow = totalRevenues - totalOpex - netInvestment;

  // 9. ROI jaar 1 — of `null` als er niets te delen valt.
  //
  // Tot 8 september 2026 stond hier `let roiYear1 = 0`, waarna de deling alleen
  // werd uitgevoerd bij `netInvestment > 0`. Die nul reisde daarna mee als
  // ware het een meting: 12 van de 27 opgeslagen businesscases hadden
  // roi_year1 = 0, en bij 6 daarvan is de netto investering zelf 0 — daar is
  // geen rendement van 0% berekend, daar is niets berekend. Core 1.4MW kreeg zo
  // op scherm en in de PDF het oordeel "hoog risico" bij 420.828 euro omzet,
  // 242.447 euro positieve kasstroom en een terugverdientijd van 1 jaar.
  //
  // `null` betekent hier: niet te berekenen. `beoordeelBusinesscase` geeft dan
  // geen oordeel (niveau 'onbekend') in plaats van een slecht oordeel, en
  // formatPercentage toont een streepje in plaats van 0,0%.
  const year1Data = yearlyData.length > 0 ? yearlyData[0] : null;

  let roiYear1: number | null = null;
  if (year1Data && netInvestment > 0) {
    if (roiMode === 'netto') {
      // Netto ROI: (omzet - kosten) / investering
      const year1Net = year1Data.revenue - year1Data.opex;
      roiYear1 = (year1Net / netInvestment) * 100;
    } else {
      // Bruto ROI: omzet / investering (default)
      roiYear1 = (year1Data.revenue / netInvestment) * 100;
    }
  }

  // 10. ROI Scenarios (based on Year 1) — onbekend blijft onbekend.
  const roiBestCase = roiYear1 === null ? null : roiYear1 * 1.15;  // +15% best case
  const roiWorstCase = roiYear1 === null ? null : roiYear1 * 0.90;  // -10% worst case

  return {
    totalInvestment,
    eiaValue,
    netInvestment,
    totalRevenues,
    totalOpex,
    totalCashflow,
    roiYear1,
    roiBestCase,
    roiWorstCase,
    paybackPeriod,
    yearlyData,
    roiMode
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '€0';
  }
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage for display
 */
export function formatPercentage(value: number | null | undefined, decimals: number = 1): string {
  // Een streepje, geen 0,0%. Tot 8 september 2026 gaf deze functie bij een
  // ontbrekende waarde "0.0%" terug — een getal dat niet gemeten is, gepresenteerd
  // als meting. Dat is dezelfde stille nul die roiYear1 zelf had; hem hier
  // opnieuw invullen zou de reparatie daar meteen ongedaan maken.
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Get color based on ROI value
 */
export function getROIColor(roi: number | null | undefined): string {
  if (roi === null || roi === undefined) {
    return '#6b7280'; // gray for null/undefined
  }
  if (roi >= 20) return '#22c55e'; // green
  if (roi >= 10) return '#84cc16'; // lime
  if (roi >= 5) return '#eab308'; // yellow
  if (roi >= 0) return '#f97316'; // orange
  return '#ef4444'; // red
}
